// Web Push client glue. Sits between the browser's PushManager and
// Haiven's backend, exposing a small, testable surface for the prompt
// component + Profile toggle to call. None of these functions throw —
// every method catches its own errors and returns either an explicit
// status object or null so the caller can show graceful UI without
// try/catch noise.

import { api } from "../api/client";

const PWA_REGISTRATION_PATH = "/sw.js";


export function isSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}


// Best-effort detection that the PWA is running in standalone mode.
// iOS only delivers Web Push to home-screen-installed PWAs, so we
// gate prompts on this — asking permission inside Safari proper on
// iOS produces a "denied" return that we can never recover from.
export function isStandalone() {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia?.("(display-mode: standalone)").matches;
  // ``window.navigator.standalone`` is the iOS-Safari-only legacy
  // flag — still the only reliable indicator on older iOS builds.
  const iosStandalone = window.navigator?.standalone === true;
  return Boolean(mq || iosStandalone);
}


// VAPID base64url → Uint8Array. The browser PushManager wants the
// public key as a typed array, not a base64 string. Standard
// transformation, doesn't depend on any library.
function _urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}


function _detectPlatform() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}


export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(PWA_REGISTRATION_PATH, {
      scope: "/",
    });
  } catch (e) {
    console.warn("[push] service worker registration failed", e);
    return null;
  }
}


export async function getVapidPublicKey() {
  try {
    const resp = await api.get("/push/vapid-key");
    return resp?.public_key || null;
  } catch (e) {
    // 503 = key not configured server-side. Caller renders the
    // "notifications unavailable" state and we never prompt.
    console.warn("[push] vapid-key fetch failed", e);
    return null;
  }
}


export async function subscribeToPush(registration) {
  if (!registration?.pushManager) return null;
  const publicKey = await getVapidPublicKey();
  if (!publicKey) return null;

  try {
    // Always register fresh rather than letting the browser hand back
    // a pre-existing subscription. This is what makes the Profile
    // toggle's off/on a real re-subscribe path — without it, a stale
    // subscription (e.g. one created before the standalone-mode gate
    // below existed) would just get silently reused.
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe().catch(() => {});
    }
    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: _urlBase64ToUint8Array(publicKey),
    });
  } catch (e) {
    console.warn("[push] pushManager.subscribe failed", e);
    return null;
  }
}


export async function saveSubscription(subscription, token) {
  if (!subscription) return false;
  const json = subscription.toJSON();
  try {
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      platform: _detectPlatform(),
    }, token);
    return true;
  } catch (e) {
    console.warn("[push] save subscription failed", e);
    return false;
  }
}


export async function unsubscribe(token) {
  // Pull the current subscription from the browser's PushManager, drop
  // the row on the backend, then revoke the browser-side
  // subscription. Returns true if at least the server-side delete
  // succeeded — what the UI needs to flip its toggle.
  if (!("serviceWorker" in navigator)) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager?.getSubscription();
  if (!sub) return true;  // nothing to do; already unsubscribed.

  try {
    await api.delete("/push/unsubscribe", token, {
      endpoint: sub.endpoint,
    });
  } catch (e) {
    console.warn("[push] server unsubscribe failed", e);
    return false;
  }
  try {
    await sub.unsubscribe();
  } catch {
    /* swallow — browser-side revoke is best-effort */
  }
  return true;
}


// Composite flow used by the prompt UI: register → prompt → subscribe
// → persist. Returns a status string the caller can branch on:
//   'subscribed' | 'denied' | 'unsupported' | 'no-vapid' | 'install-required' | 'error'
export async function enablePushNotifications(token) {
  if (!isSupported()) return "unsupported";
  // iOS only ever delivers Web Push to a home-screen-installed PWA —
  // subscribing from a bare Safari tab succeeds all the way through
  // (permission grant, PushManager.subscribe, backend save, even the
  // backend's send call against the resulting endpoint) but the OS
  // never wakes for it, which is exactly the "backend sends fine,
  // device never gets it" symptom. Block before burning the one-shot
  // permission prompt on a subscription that can't ever deliver.
  if (_detectPlatform() === "ios" && !isStandalone()) return "install-required";
  const reg = await registerServiceWorker();
  if (!reg) return "error";

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";

  const sub = await subscribeToPush(reg);
  if (!sub) return "no-vapid";

  const ok = await saveSubscription(sub, token);
  return ok ? "subscribed" : "error";
}


// ── Native (Capacitor iOS) path ──────────────────────────────────────
// The @capacitor/push-notifications plugin was installed and configured
// but never wired: native builds have no serviceWorker, so isSupported()
// is false and every Web Push surface silently no-ops inside the app
// shell. The backend has carried /push/apns-subscribe for exactly this
// token since the wearable-nudge work — this is the client half.
//
// Static import, not import(): a dynamic import() creates the app's
// only code-split chunk and stalls forever in WKWebView on physical
// devices (the Build 9 HealthKit lesson — see services/healthkit.js).
import { PushNotifications as NativePush } from "@capacitor/push-notifications";
import { isNativeIOS } from "../utils/platform";

const NATIVE_REGISTRATION_TIMEOUT_MS = 10_000;

export function isPushCapable() {
  return isNativeIOS() || isSupported();
}

// Same status contract as enablePushNotifications:
//   'subscribed' | 'denied' | 'unsupported' | 'error'
export async function enableNativePush(token) {
  if (!isNativeIOS()) return "unsupported";
  try {
    const perm = await NativePush.requestPermissions();
    if (perm.receive !== "granted") return "denied";

    // register() resolves before APNs hands back the device token — the
    // token arrives on the 'registration' listener. Race it against a
    // timeout so a hung APNs round-trip can't trap the pre-prompt UI.
    const deviceToken = await new Promise((resolve) => {
      let settled = false;
      const settle = (v) => { if (!settled) { settled = true; resolve(v); } };
      const timer = setTimeout(() => settle(null), NATIVE_REGISTRATION_TIMEOUT_MS);
      NativePush.addListener("registration", (t) => {
        clearTimeout(timer);
        settle(t?.value || null);
      });
      NativePush.addListener("registrationError", () => {
        clearTimeout(timer);
        settle(null);
      });
      NativePush.register().catch(() => { clearTimeout(timer); settle(null); });
    });
    if (!deviceToken) return "error";

    await api.post("/push/apns-subscribe", {
      device_token: deviceToken,
      platform: "ios_apns",
    }, token);
    return "subscribed";
  } catch (e) {
    console.warn("[push] native enable failed", e);
    return "error";
  }
}

// Platform-branched composite the UI calls — native shell gets APNs,
// everything else gets the Web Push flow above.
export async function enablePush(token) {
  return isNativeIOS() ? enableNativePush(token) : enablePushNotifications(token);
}
